// src/components/Pentests/tabs/FindingForm.js
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';

const SEVERITY_LABELS = {
  info: 'Info',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

const SEVERITY_BADGE = {
  info: 'bg-slate-100 text-slate-700',
  low: 'bg-blue-100 text-blue-700',
  medium: 'bg-yellow-100 text-yellow-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

const COMPLEXITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High' };

function severityFromCvss(cvss) {
  if (cvss == null || cvss === '' || isNaN(cvss)) return 'info';
  const v = Number(cvss);
  if (v <= 0) return 'info';
  if (v <= 3.9) return 'low';
  if (v <= 6.9) return 'medium';
  if (v <= 8.9) return 'high';
  return 'critical';
}

function normalizeCwe(raw) {
  const t = String(raw).trim().toUpperCase();
  if (!t) return null;
  if (/^CWE-\d+$/.test(t)) return t;
  if (/^\d+$/.test(t)) return `CWE-${t}`;
  return null;
}

export default function FindingForm({ initial, onCancel, onSubmit, submitting }) {
  const isEdit = !!initial;
  const [title, setTitle] = useState('');
  const [cweInput, setCweInput] = useState('');
  const [cweIds, setCweIds] = useState([]);
  const [cvss, setCvss] = useState('');
  const [severityManual, setSeverityManual] = useState(false);
  const [severityOverride, setSeverityOverride] = useState('info');
  const [scopeDescription, setScopeDescription] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [complexity, setComplexity] = useState('');
  const [priority, setPriority] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [cweError, setCweError] = useState(null);

  useEffect(() => {
    if (!initial) return;
    setTitle(initial.title || '');
    setCweIds(initial.cwe_ids || []);
    setCvss(initial.cvss_score == null ? '' : String(initial.cvss_score));
    setSeverityManual(!!initial.severity_manual);
    setSeverityOverride(initial.severity || 'info');
    setScopeDescription(initial.scope_description || '');
    setRecommendation(initial.recommendation || '');
    setComplexity(initial.complexity || '');
    setPriority(initial.priority == null ? '' : String(initial.priority));
  }, [initial]);

  const previewSeverity = useMemo(
    () => (severityManual ? severityOverride : severityFromCvss(cvss)),
    [severityManual, severityOverride, cvss],
  );

  const tryAddCwe = (raw) => {
    const norm = normalizeCwe(raw);
    if (!norm) {
      setCweError('Use CWE-### or just ### (e.g. CWE-798 or 798)');
      return;
    }
    setCweError(null);
    if (!cweIds.includes(norm)) {
      setCweIds([...cweIds, norm]);
    }
    setCweInput('');
  };

  const onCweKeyDown = (e) => {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      if (cweInput.trim()) tryAddCwe(cweInput);
    } else if (e.key === 'Backspace' && !cweInput && cweIds.length > 0) {
      setCweIds(cweIds.slice(0, -1));
    }
  };

  const removeCwe = (val) => setCweIds(cweIds.filter((c) => c !== val));

  const handleSubmit = (e) => {
    e.preventDefault();
    const titleTrim = title.trim();
    if (!titleTrim) return;
    const cvssNum = cvss === '' ? null : Number(cvss);
    if (cvssNum != null && (isNaN(cvssNum) || cvssNum < 0 || cvssNum > 10)) return;
    const payload = {
      title: titleTrim,
      cwe_ids: cweIds,
      cvss_score: cvssNum,
      severity_manual: severityManual,
      severity: severityManual ? severityOverride : null,
      scope_description: scopeDescription,
      recommendation: recommendation,
      complexity: complexity || null,
      priority: priority === '' ? null : Number(priority),
    };
    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-2xl max-h-[90vh] overflow-auto rounded-2xl border border-cyber-300/60 bg-cyber-50 shadow-2xl"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-cyber-300/40">
          <h2 className="text-lg font-semibold text-slate-900">
            {isEdit ? `Edit Finding #${initial.reference_number}` : 'Add Finding'}
          </h2>
          <button type="button" onClick={onCancel} className="p-1 rounded text-slate-500 hover:text-slate-900">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-800 mb-2">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 text-slate-900 focus:border-shinobi-green outline-none"
                required
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-800 mb-2">CWE</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {cweIds.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-cyber-200 border border-cyber-300/40 text-slate-800">
                    {c}
                    <button type="button" onClick={() => removeCwe(c)} className="text-slate-500 hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                value={cweInput}
                onChange={(e) => setCweInput(e.target.value)}
                onKeyDown={onCweKeyDown}
                onBlur={() => cweInput.trim() && tryAddCwe(cweInput)}
                placeholder="CWE-798 (Enter or comma to add)"
                className="w-full bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 text-slate-900 focus:border-shinobi-green outline-none"
              />
              {cweError && <p className="text-xs text-red-600 mt-1">{cweError}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-800 mb-2">CVSS Score</label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.1"
                  value={cvss}
                  onChange={(e) => setCvss(e.target.value)}
                  className="w-24 bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 text-slate-900 focus:border-shinobi-green outline-none"
                />
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="0.1"
                  value={cvss === '' ? 0 : cvss}
                  onChange={(e) => setCvss(e.target.value)}
                  className="flex-1 accent-shinobi-green"
                />
                <span className={`px-2 py-0.5 text-xs rounded ${SEVERITY_BADGE[previewSeverity]}`}>
                  {SEVERITY_LABELS[previewSeverity]}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs">
                <input
                  id="severity-override"
                  type="checkbox"
                  checked={severityManual}
                  onChange={(e) => setSeverityManual(e.target.checked)}
                  className="accent-shinobi-green"
                />
                <label htmlFor="severity-override" className="text-slate-700">Override severity</label>
                {severityManual && (
                  <select
                    value={severityOverride}
                    onChange={(e) => setSeverityOverride(e.target.value)}
                    className="ml-2 bg-cyber-100 border border-cyber-300/60 rounded px-2 py-0.5 text-slate-900 focus:border-shinobi-green outline-none"
                  >
                    {Object.keys(SEVERITY_LABELS).map((s) => (
                      <option key={s} value={s}>{SEVERITY_LABELS[s]}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-cyber-300/30 pt-4">
            <button
              type="button"
              onClick={() => setDetailsOpen((o) => !o)}
              className="flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-slate-900"
            >
              {detailsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Details
            </button>
            {detailsOpen && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-800 mb-2">Scope description</label>
                  <textarea
                    value={scopeDescription}
                    onChange={(e) => setScopeDescription(e.target.value)}
                    rows={3}
                    className="w-full bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 text-slate-900 focus:border-shinobi-green outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-800 mb-2">Recommendation</label>
                  <textarea
                    value={recommendation}
                    onChange={(e) => setRecommendation(e.target.value)}
                    rows={3}
                    className="w-full bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 text-slate-900 focus:border-shinobi-green outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-800 mb-2">Complexity</label>
                    <select
                      value={complexity}
                      onChange={(e) => setComplexity(e.target.value)}
                      className="w-full bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 text-slate-900 focus:border-shinobi-green outline-none"
                    >
                      <option value="">- not set -</option>
                      {Object.entries(COMPLEXITY_LABELS).map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-800 mb-2">Priority</label>
                    <select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      className="w-full bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 text-slate-900 focus:border-shinobi-green outline-none"
                    >
                      <option value="">- not set -</option>
                      <option value="1">1 (highest)</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5 (lowest)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-cyber-300/40 bg-cyber-100/40">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg text-slate-700 hover:text-slate-900">
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !title.trim()}
            className="px-4 py-2 rounded-lg bg-shinobi-green text-cyber-900 font-medium hover:brightness-110 disabled:opacity-50"
          >
            {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Finding'}
          </button>
        </div>
      </form>
    </div>
  );
}
